/* eslint-disable sonarjs/cognitive-complexity */
import cn from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { browserName, fullBrowserVersion } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Chat from 'src/components/base-components/Chat/Chat';
import { SvgIcon } from 'src/components/common/SvgIcon';
import Tooltip from 'src/components/common/Tooltip';
import { envBoolean, getDeviceName, scrollToTop, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useGameAction } from 'src/hooks/useGameAction';
import { MediaManager } from 'src/manager/MediaManager';
import { getMobileFixedMenuData } from 'src/services/configsApi';
import PersisterService from 'src/services/PersisterService';
import {
  resetSectionLoading,
  setSectionLoading,
  setSelectedCategory,
  setSelectedFooterMenuItem,
  setShowUserSettings,
} from 'src/store/app/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setSelectedGame, setSelectedProvider, setSelectedTag } from 'src/store/games/actions';
import {
  setMobileLiveChatVisibility,
  toggleMobileMenu,
  toggleMobileProvider,
  toggleMobileSearch,
} from 'src/store/mobile/actions';
import { MobileFixedMenuItem } from 'src/types/res-dto/mobile-res';
import { RootState } from 'src/types/store-types';
import { SPORT_CATEGORIES } from 'src/utils/constants';
import _styles from './styles.module.scss';

const MobileFixedMenu: FC = () => {
  const { t }: Translation = useTranslation();

  const navigate = useNavigate();

  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _toggleMobileSearch = useConnectedAction(toggleMobileSearch);
  const _toggleMobileProvider = useConnectedAction(toggleMobileProvider);
  const _setSelectedFooterMenuItem = useConnectedAction(setSelectedFooterMenuItem);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const _toggleMobileMenu = useConnectedAction(toggleMobileMenu);
  const _setShowUserSettings = useConnectedAction(setShowUserSettings);
  const _setMobileLiveChatVisibility = useConnectedAction(setMobileLiveChatVisibility);
  const _selectedGame = useConnectedAction(setSelectedGame);
  const _resetSectionLoading = useConnectedAction(resetSectionLoading);
  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _setSectionLoading = useConnectedAction(setSectionLoading);
  const _openDialog = useConnectedAction(openDialog);

  const { showMenu, showSearch, showProviders } = useSelector((state: RootState) => state.mobile);
  const { selectedCategory, selectedFooterMenu, showUserSetting } = useSelector((state: RootState) => state.app);
  const { user } = useSelector((state: RootState) => state.user);

  const { generalConfigs } = useSelector((state: RootState) => state.configs);

  const [menuItems, setMenuItems] = useState<MobileFixedMenuItem[]>([]);
  const [showBurgerMenu, setShowBurgerMenu] = useState<boolean>(false);
  const hasLabel = menuItems?.some((item) => item?.label);
  const { shouldShowLimits, shouldShowKyc } = useGameAction();

  const hideFixedMenu =
    SPORT_CATEGORIES.includes(selectedCategory) &&
    (envBoolean(process.env.REACT_APP_HIDE_SPORT) || envBoolean(process.env.REACT_APP_CMS_WAGER));

  const selectCategory = (category: MobileFixedMenuItem): void => {
    _resetSectionLoading();

    if (category.needLogin && !user) {
      _openAuthModal('login');
      _setSectionLoading(false);
      setBodyOverflow('unset');
    } else {
      if (category.type === 'game') {
        if (shouldShowKyc) {
          _openDialog({
            dialogType: EnumDialogsKeys.KYC_POPUP,
            dialogProps: { data: {} },
          });
        } else if (shouldShowLimits) {
          _openDialog({
            dialogType: EnumDialogsKeys.SET_LIMITS_AFTER_LOGIN_POPUP,
            dialogProps: { data: {} },
          });
        } else {
          _selectedGame({ id: category?.gameId });
        }
      } else if (category.type === 'content') {
        navigate(`/content/${category.slug}`);
        _setSelectedCategory(category.name);
      } else {
        const selectedCategory = category.name;
        _setSelectedCategory(selectedCategory);
        _setSelectedProvider(['all_providers']);
        _setSelectedTag(category.hasHomeTag ? 'home' : 'allGames');
        navigate(`/category/${category.slug}`);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('onEmbeddedMessagingWindowMinimized', () => {
      const iframe = window.embeddedservice_bootstrap.utilAPI.getEmbeddedMessagingFrame();
      if (iframe) {
        iframe.style.display = 'none';
      }
    });
  }, []);

  const selectMenuItem = (menuItem: any): void => {
    if (menuItem.type === 'chat') {
      _setSelectedFooterMenuItem('');
      if (['liveChatButton', 'liveChat'].includes(generalConfigs?.chat?.type)) {
        _setMobileLiveChatVisibility(true);
      } else if (generalConfigs.chat.type === 'comm100') {
        (window as any)?.openComm100Chat?.();
      } else if (generalConfigs.chat.type === 'tawkChat') {
        (window as any)?.Tawk_API.maximize();
      } else if (generalConfigs.chat.type === 'zendesk') {
        (window as any)?.zE('messenger', 'open');
      } else {
        const iframe = window.embeddedservice_bootstrap.utilAPI?.getEmbeddedMessagingFrame();
        if (window.embeddedservice_bootstrap?.prechatAPI) {
          window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
            Browser_Name: `${browserName} ${fullBrowserVersion}`,
            Device_Name: getDeviceName(),
          });
        }
        window.embeddedservice_bootstrap.utilAPI?.launchChat().then(() => {
          if (iframe) {
            iframe.style.zIndex = 215;
            iframe.style.display = 'block';
          }
        });
      }
    } else if (menuItem.type === 'burgerMenu') {
      setShowBurgerMenu(true);
    } else {
      _setSelectedFooterMenuItem(menuItem.name);
      selectCategory(menuItem);
      setShowBurgerMenu(false);
    }

    showUserSetting && _setShowUserSettings();
    showMenu && _toggleMobileMenu();
    showProviders && _toggleMobileProvider();
    showSearch && _toggleMobileSearch();

    menuItem.type !== 'burgerMenu' && scrollToTop();
  };

  const getMobileFixedMenu = async (): Promise<void> => {
    let res: MobileFixedMenuItem[] | any = null;

    if (PersisterService.hasPersistValue('mobiledFixedMenu')) {
      res = PersisterService.getPersistValue('mobiledFixedMenu');
      setMenuItems(res);
    } else {
      res = await getMobileFixedMenuData();

      if (res.success) {
        PersisterService.setPersistValue('mobiledFixedMenu', res.result);
        setMenuItems(res.result);
      }
    }
  };

  useEffect(() => {
    getMobileFixedMenu().catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    !showMenu && _setSelectedFooterMenuItem('');
    showMenu && setShowBurgerMenu(false);
  }, [showMenu, showBurgerMenu]);

  useEffect(() => {
    _setSelectedFooterMenuItem(
      Boolean(menuItems?.filter((f) => f.name === selectedCategory).length) ? selectedCategory : null
    );
  }, [selectedCategory, selectedFooterMenu, menuItems]);

  const renderMenuItem = (menuItem: { imageSrcName: string }): JSX.Element => {
    if (
      [
        'casinoTournament',
        'tigerFortune',
        'poker',
        'fun-mission',
        'race',
        'bingo',
        'cybersport',
        'liveSport',
        'pGsoft',
        'raffle-draw',
        'slots',
        'burgerMenuIcon',
        'fsRace',
        'copaPapi',
        'loyalty',
      ].includes(menuItem.imageSrcName)
    ) {
      return <SvgIcon src={MediaManager.getSrcFromMediaAssets(`${menuItem.imageSrcName}.svg`, '/navigation-icons/')} />;
    }

    return <span className={`icon-${menuItem.imageSrcName}`}></span>;
  };

  const menuItemButton = (menuItem: any, type = ''): JSX.Element => {
    return (
      <>
        {menuItem?.label && <span className={_styles.nav_label}>{t(menuItem?.label)}</span>}
        <button key={menuItem.name} className={_styles.element} onClick={() => selectMenuItem(menuItem)}>
          {renderMenuItem(menuItem)}
          {t(`category_${menuItem.name}${menuItem.type === 'game' ? 'game' : ''}`)}
          {selectedFooterMenu === menuItem.name && type !== 'burgerMenu' && <span className={_styles.active} />}
        </button>
      </>
    );
  };

  return (
    <div
      className={cn(_styles.mobile_fixed_menu_container, 'mobile_fixed_menu_container', {
        [_styles.hide_fixed_menu]: hideFixedMenu,
      })}
    >
      {menuItems.length > 0 &&
        menuItems?.map((menuItem: MobileFixedMenuItem, i: number) => {
          return (
            <div
              className={cn(_styles.btn_container, {
                [_styles.burgerMenuTop]: hasLabel && !menuItem?.label,
              })}
              key={menuItem.name + i}
            >
              {menuItem.type === 'burgerMenu' ? (
                <Tooltip
                  data={menuItem?.burgerMenu.map((item: any, index: number) => (
                    <span className="burgerMenu__item" key={item.name + index}>
                      {menuItemButton(item, menuItem.type)}
                    </span>
                  ))}
                  position="top"
                  className="burgerMenu"
                  showBurgerMenu={showBurgerMenu}
                  setShowBurgerMenu={setShowBurgerMenu}
                >
                  {menuItemButton(menuItem, menuItem.type)}
                </Tooltip>
              ) : (
                menuItemButton(menuItem)
              )}
            </div>
          );
        })}
      {!!generalConfigs?.chat && <Chat configs={generalConfigs?.chat} placement="mobileFixedMenu" />}
    </div>
  );
};
export default MobileFixedMenu;
